// VerificationBrief — Lisa's first-person buyer verification brief.
//
// Drop-in replacement for the old 7-dimension scorecard. Renders the JSON
// payload returned by POST /api/re/verification/enrich.

import { useState } from 'react';
import { Check, Minus, Sparkles, X } from 'lucide-react';

const TIER_META = {
  LIKELY_REAL: {
    label: 'LIKELY REAL',
    bg: 'bg-[#22c55e]',
    ring: 'ring-[#22c55e]/30',
  },
  MOSTLY_REAL: {
    label: 'MOSTLY REAL',
    bg: 'bg-[#eab308]',
    ring: 'ring-[#eab308]/30',
  },
  CANT_VERIFY: {
    label: "CAN'T VERIFY",
    bg: 'bg-[#6b7280]',
    ring: 'ring-[#6b7280]/30',
  },
  LIKELY_FAKE: {
    label: 'LIKELY FAKE',
    bg: 'bg-[#ef4444]',
    ring: 'ring-[#ef4444]/30',
  },
};

function tierMeta(tier) {
  return TIER_META[tier] || TIER_META.CANT_VERIFY;
}

export default function VerificationBrief({ brief, name }) {
  const [deepCheckOpen, setDeepCheckOpen] = useState(false);

  if (!brief) return null;

  const meta = tierMeta(brief.tier);
  const checksOut = Array.isArray(brief.checks_out) ? brief.checks_out : [];
  const cantConfirm = Array.isArray(brief.cant_confirm) ? brief.cant_confirm : [];
  const worthKnowing = Array.isArray(brief.worth_knowing) ? brief.worth_knowing : [];

  return (
    <div className="card p-6 md:p-8 space-y-6">
      {/* Header — Lisa avatar + verified [name] */}
      <div className="flex items-center gap-3">
        <img
          src="/chat-avatar.png"
          alt="Lisa"
          className="w-10 h-10 rounded-full shrink-0 border border-border-card bg-bg-subtle object-cover"
        />
        <p className="text-sm md:text-base font-medium text-text-primary">
          Lisa verified{' '}
          <span className="font-semibold">{name || 'this buyer'}</span>
        </p>
      </div>

      {/* Tier badge + score */}
      <div className="flex flex-col items-center gap-3 text-center">
        <span
          className={`${meta.bg} text-white font-semibold tracking-wider uppercase
                     px-6 py-2 rounded-pill text-sm md:text-base ring-4 ${meta.ring}`}
        >
          {meta.label}
        </span>
        <div className="text-5xl md:text-6xl font-bold text-text-primary tabular-nums">
          {brief.score}
        </div>
        <div className="text-[11px] uppercase tracking-wider text-text-secondary">
          Confidence score · 0–100
        </div>
      </div>

      {/* Verdict line */}
      {brief.verdict_line && (
        <p className="text-base md:text-lg text-text-primary leading-relaxed text-center max-w-xl mx-auto">
          {brief.verdict_line}
        </p>
      )}

      {checksOut.length > 0 && (
        <BriefList
          heading="What checks out"
          items={checksOut}
          bullet={
            <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-verified/15 flex items-center justify-center">
              <Check size={12} className="text-verified" strokeWidth={3} />
            </span>
          }
        />
      )}

      {cantConfirm.length > 0 && (
        <BriefList
          heading="What I couldn't confirm"
          items={cantConfirm}
          bullet={
            <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-cold/15 flex items-center justify-center">
              <Minus size={12} className="text-cold" strokeWidth={3} />
            </span>
          }
        />
      )}

      {worthKnowing.length > 0 && (
        <BriefList
          heading="Worth knowing"
          items={worthKnowing}
          bullet={
            <span className="shrink-0 mt-1.5 w-2 h-2 rounded-full bg-accent" />
          }
        />
      )}

      {/* My take */}
      <section>
        <h3 className="text-[11px] uppercase tracking-wider font-semibold text-text-secondary mb-2">
          My take
        </h3>
        <p className="text-sm md:text-base text-text-primary leading-relaxed">
          {brief.my_take}
        </p>
      </section>

      <hr className="border-border-card" />

      {/* CRM sync line */}
      {brief.crm_sync_message && (
        <p className="text-sm italic text-text-secondary leading-relaxed">
          {brief.crm_sync_message}
        </p>
      )}

      {/* Deep Check CTA */}
      <div>
        <button
          type="button"
          onClick={() => setDeepCheckOpen(true)}
          className="inline-flex items-center gap-2 rounded-outlined border-2 border-accent
                     bg-white text-accent hover:bg-accent/[0.06] font-semibold
                     px-5 py-2.5 text-sm transition-colors"
        >
          <Sparkles size={15} />
          Run Deep Check — $50
        </button>
      </div>

      <hr className="border-border-card" />

      <p className="text-xs text-text-secondary leading-relaxed">
        Based on publicly available information and documents you provided.
        Always conduct independent due diligence before any transaction.
      </p>

      {deepCheckOpen && <DeepCheckModal onClose={() => setDeepCheckOpen(false)} />}
    </div>
  );
}

function BriefList({ heading, items, bullet }) {
  return (
    <section>
      <h3 className="text-[11px] uppercase tracking-wider font-semibold text-text-secondary mb-3">
        {heading}
      </h3>
      <ul className="space-y-2">
        {items.map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-sm text-text-primary leading-relaxed">
            {bullet}
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DeepCheckModal({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-card shadow-card-hover max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 p-1 rounded-btn hover:bg-bg-subtle text-text-secondary"
        >
          <X size={18} />
        </button>
        <h2 className="text-lg font-semibold text-text-primary mb-3">
          Deep Check — coming soon
        </h2>
        <p className="text-sm text-text-primary leading-relaxed">
          For $50, Lisa will run a deeper verification that includes:
        </p>
        <ul className="mt-3 space-y-1.5 text-sm text-text-primary">
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
            Court records (civil, criminal, eviction history)
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
            Property ownership records nationwide
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
            Identity and address history verification
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
            Business entity and officer lookups
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
            Advanced financial footprint analysis
          </li>
        </ul>
        <p className="mt-4 text-sm text-text-primary">
          We're rolling this out in the next two weeks. Want early access?
        </p>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-outlined border-2 border-border-card
                       bg-white text-text-primary px-4 py-2 text-sm font-semibold
                       hover:border-text-muted transition-colors"
          >
            Close
          </button>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center rounded-pill bg-accent hover:bg-accent-hover
                       text-white font-semibold px-5 py-2 text-sm transition-colors"
          >
            Notify me when it's ready
          </button>
        </div>
      </div>
    </div>
  );
}
